<template>
  <div class="container">
    <BLoading :active.sync="isLoading" />
    <div v-if="!isLoading">
      <div class="required-service-view--back-link-container">
        <RouterLink
          test-id="back_link"
          class="required-service-view--back-link"
          :to="{ name: 'services' }"
        >
          <LsIcon class="required-service-view--chevron" type="chevron" size="md" />BACK
        </RouterLink>
      </div>
      <div v-if="status === 'expired'" class="required-service-view--header-container">
        <h1 class="required-service-view--expired-header">
          <LsIcon type="cancel" class="required-service-view--icon" size="md" color="danger" />
          This quote expired on {{ getInfo.endStatus }}
        </h1>
      </div>
      <div class="columns column-reverse">
        <div class="column">
          <h4 class="required-services-view--header-main">
            <span class="has-text-weight-bold"> {{ getInfo.headerMain }} </span
            >{{ getInfo.startStatus }}
          </h4>

          <h4>
            <LsIcon type="quote" size="lg" class="required-service-view--quote-icon" />
            <span class="required-service-view--price">{{ getInfo.priceMain }} </span>to complete
            {{ getInfo.serviceName }}
          </h4>
          <LsTypicalPricing
            v-if="status === 'ready'"
            :property-id="property_id"
            :service-id="getInfo.serviceId"
            priced
          />
        </div>
        <div class="column is-narrow required-service-view--address">
          <LsAddressCard :property="selectedProperty" />
        </div>
      </div>
      <div
        :class="
          status === 'expired' || status === 'rejected'
            ? 'required-service-view--details-container-expired'
            : 'required-service-view--details-container-ready'
        "
      >
        <h4 class="required-services-view--required-text">
          This work is required to complete other services
        </h4>
        <p v-if="status === 'approved'" class="has-text-weight-bold required-service-view--details">
          <LsIcon type="checkmark" class="required-service-view--icon" />You approved this work on
          {{ getInfo.statusSetDate }}
        </p>

        <p
          v-if="status === 'rejected'"
          class="has-text-black required-service-view--details-expired"
        >
          <LsIcon type="cancel" class="required-service-view--icon" color="danger" />
          You turned down this quote on
          <span class="has-text-weight-bold">{{ getInfo.statusSetDate }}</span>
        </p>
        <p
          v-if="status === 'expired'"
          class="has-text-black required-service-view--details-expired"
        >
          <LsIcon type="cancel" class="required-service-view--icon" color="danger" />
          This quote expired on <span class="has-text-weight-bold">{{ getInfo.endStatus }}</span>
        </p>
        <p v-if="status === 'ready'" class="required-service-view--details">
          <LsIcon type="calendar" class="required-service-view--calendar-icon" />Pending your
          approval, {{ getInfo.serviceName }}, will be scheduled between
          <span class="has-text-weight-bold">
            {{ getInfo.scheduledStart }} - {{ getInfo.scheduledEnd }}</span
          >
        </p>
        <p class="required-service-view--work-details">
          <span class="required-service-view--work-details-text">{{
            selectedService.description
          }}</span>
        </p>
        <div
          v-if="selectedService.images.length"
          :class="
            status === 'expired' || status === 'rejected'
              ? 'columns required-service-view--image-container-expired'
              : 'columns required-service-view--image-container-ready'
          "
        >
          <LsImage
            v-for="(image, idx) in selectedService.images"
            :key="idx"
            class="column required-service-view--images"
            :src="image.image_url"
          />
        </div>

        <div v-if="status === 'ready'" class="required-service-view--details-last">
          <p class="has-text-weight-bold">This quote is valid until {{ getInfo.endStatus }}</p>
          <p>As always, your credit card will not be charged until after service is complete</p>
        </div>
      </div>

      <div v-if="status === 'ready'" class="required-service-view--footer">
        <LsButton
          text="Approve Work"
          test-id="approve-work"
          track-id="required_service_view-approve_work"
          class="required-service-view--button"
          :loading="approveWorkLoading"
          @click="approveWork"
        />

        <LsButton
          text="Ask Question"
          test-id="ask-question"
          bg-color="primary-light"
          text-color="primary"
          class="required-service-view--button"
          track-id="required_service_view-ask_question"
          :loading="askQuestionsLoading"
          @click="askQuestion"
        />
        <LsButton
          text="Turn Down"
          test-id="turn-down"
          bg-color="primary-light"
          text-color="primary"
          class="required-service-view--button"
          track-id="required_service_view-turn_down"
          @click="turnDown"
        />
      </div>

      <LsScheduleServiceItem
        v-if="status === 'approved'"
        :schedule="selectedService.schedule"
        class="required-service-view--schedule-service-item"
        track-id="services_view-schedule_service_item"
        divider-bottom
        divider-top
        @click="
          $router.push({
            name: 'serviceHistory',
            params: {
              property_id: property_id,
              schedule_id: selectedService.schedule.schedule_id,
            },
          })
        "
      />
    </div>
  </div>
</template>

<script>
import { LsButton, LsAddressCard, LsIcon, LsImage } from '@/components/common';
import { LsTypicalPricing, LsScheduleServiceItem } from '@/components';
import {
  PROPERTIES__SELECTED_PROPERTY,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  REQUIRED_SERVICES__GET_REQUIRED_SERVICE,
  REQUIRED_SERVICES__APPROVE_QUOTE,
  CONVERSATION__START_CONVERSATION,
} from '@/store/types';
import { mapState, mapGetters, mapActions } from 'vuex';
import _ from 'lodash';
import { lsModalService, lsRouterHelpers } from 'ls-vue-common';
import { generateRequiredContent } from './RequiredServiceViewHelper';
import { TurnDownQuoteModal } from '@/modals';

export default {
  name: 'RequiredServiceView',
  components: {
    LsButton,
    LsAddressCard,
    LsIcon,
    LsTypicalPricing,
    LsImage,
    LsScheduleServiceItem,
  },
  data() {
    return {
      property_id: lsRouterHelpers.getParam(this.$route, 'property_id'),
      requiredService_id: lsRouterHelpers.getParam(this.$route, 'requiredService_id'),
      status: '',
      approveWorkLoading: false,
      askQuestionsLoading: false,
    };
  },
  computed: {
    ...mapState(['properties', 'screenSize', 'requiredServices', 'conversation']),
    ...mapGetters([PROPERTIES__SELECTED_PROPERTY]),
    isLoading() {
      return (
        this.properties.loadingStatus.isLoading ||
        this.requiredServices.loadingStatus.isLoading ||
        _.isNil(this.selectedProperty) ||
        _.isNil(this.getInfo) ||
        _.isNil(this.selectedService)
      );
    },
    selectedProperty() {
      return this[PROPERTIES__SELECTED_PROPERTY](this.property_id);
    },
    selectedService() {
      return this.requiredServices.requiredServicesById[_.toNumber(this.requiredService_id)];
    },

    getInfo() {
      const {
        contractorFirstName,
        headerMain,
        startStatus,
        priceMain,
        endStatus,
        hoursNeeded,
        serviceName,
        serviceId,
        scheduledStart,
        scheduledEnd,
        statusSetDate,
      } = generateRequiredContent({
        requiredServices: this.selectedService,
      });
      return {
        contractorFirstName,
        headerMain,
        startStatus,
        priceMain,
        endStatus,
        hoursNeeded,
        serviceName,
        serviceId,
        scheduledStart,
        scheduledEnd,
        statusSetDate,
      };
    },
  },
  async created() {
    await this[PROPERTIES__GET_PROPERTIES_AND_SCHEDULES]();
    await this[REQUIRED_SERVICES__GET_REQUIRED_SERVICE]({
      property_id: this.property_id,
      requiredService_id: this.requiredService_id,
    });

    if (_.isNil(this.selectedProperty) || _.isNil(this.selectedService)) {
      return this.$router.push({
        name: 'services',
      });
    }

    this.getStatus();
  },
  methods: {
    ...mapActions([
      PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
      REQUIRED_SERVICES__GET_REQUIRED_SERVICE,
      REQUIRED_SERVICES__APPROVE_QUOTE,
      CONVERSATION__START_CONVERSATION,
    ]),
    async approveWork() {
      this.approveWorkLoading = true;
      await this[REQUIRED_SERVICES__APPROVE_QUOTE]({
        property_id: this.property_id,
        requiredService_id: this.requiredService_id,
        onSuccess: () => {
          this.$router.push({ name: 'services' });
        },
      });
      this.approveWorkLoading = false;
    },
    async askQuestion() {
      this.askQuestionsLoading = true;
      const { user_id } = this.selectedService.contractor.user;

      await this[CONVERSATION__START_CONVERSATION]({
        contractorUserId: user_id,
      });

      this.$router.push({
        name: 'inboxConversation',
        params: {
          conversation_id: this.conversation.currentConversation.conversation_id,
        },
      });
      this.askQuestionsLoading = false;
    },
    turnDown() {
      lsModalService.open({
        parent: this,
        component: TurnDownQuoteModal,
        props: {
          name: this.getInfo.contractorFirstName,
          contractorUserId: this.selectedService.contractor.user.user_id,
          property_id: _.toNumber(this.property_id),
          requiredService_id: _.toNumber(this.requiredService_id),
        },
      });
    },
    getStatus() {
      this.status = this.selectedService.status;
    },
  },
};
</script>

<style lang="scss" scoped>
.required-services-view--required-text {
  font-weight: bold;
  color: $gray-primary;
  margin-left: 10px;
}
@media (max-width: $mobile-max-width) {
  .column-reverse {
    display: flex;
    flex-direction: column-reverse;
  }
  .required-service-view--address {
    border-bottom: 1px solid $primary-light;
  }
}
.required-service-view--chevron {
  vertical-align: bottom;
  margin-right: 3px;
  transform: rotate(180deg);
}
.required-service-view--back-link-container {
  margin-bottom: 30px;
  @media (max-width: $mobile-max-width) {
    margin-bottom: 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid $primary-light;
  }
}
.required-service-view--back-link {
  color: $primary;
  font-weight: bold;
}

.required-service-view--details-container-ready {
  background-color: $primary-light;
  padding: 10px;
  margin-bottom: 20px;
}
.required-service-view--details-container-expired {
  background-color: $special-recommended-services-background;
  padding: 10px;
  margin-bottom: 20px;
}
.required-service-view--work-details-text {
  white-space: pre-wrap;
}
.required-service-view--work-details {
  margin-top: 10px;
  padding: 0px 10px 10px 10px;
}
.required-service-view--details-expired {
  padding: 10px;
  border-bottom: 1px solid $gray-tertiary;
}
.required-service-view--footer {
  display: flex;
  flex-wrap: wrap;
  @media (max-width: $mobile-max-width) {
    button:nth-child(2) {
      width: calc(50% - 5px);
      min-width: 120px;
      margin-right: 5px;
    }
    button:nth-child(3) {
      width: calc(50% - 5px);
      min-width: 120px;
      margin-left: 5px;
    }
  }
}
.required-service-view--button {
  margin-right: 10px;
  @media (max-width: $mobile-max-width) {
    margin-bottom: 10px;
    margin-right: 0px;
  }
}
.required-service-view--details {
  border-bottom: 1px solid $special-recommended-services;
  padding: 10px;
}
.required-service-view--details-last {
  border-top: 1px solid $special-recommended-services;
  padding: 10px;
}
.required-service-view--price {
  font-size: $size-1;
  font-weight: bold;
}
.required-services-view--header-main {
  margin-bottom: 5px;
}
.required-service-view--quote-icon {
  margin-right: 10px;
  vertical-align: text-bottom;
}
.required-service-view--images {
  display: inline-block;
  margin: 3px;
}
.required-service-view--image-container-ready {
  margin: 0px 3px;
  border-top: 1px solid $special-recommended-services;
}
.required-service-view--image-container-expired {
  margin: 0px 3px;
  border-top: 1px solid $gray-tertiary;
}
.required-service-view--calendar-icon {
  vertical-align: sub;
  margin-right: 10px;
}
.required-service-view--icon {
  margin-right: 10px;
  vertical-align: middle;
}
.required-service-view--schedule-service-item {
  padding: 13px 0;
  margin-top: 10px;
}
.required-service-view--expired-header {
  color: $gray-primary;
  margin-bottom: 15px;
}
.required-service-view--header-container {
  display: flex;
  justify-content: center;
}
.themed-app {
  .required-services-view--required-text {
    color: $theme-color-gray-darker;
  }
  @media (max-width: $mobile-max-width) {
    .required-service-view--address {
      border-color: $theme-color-primary-light;
    }
  }
  .required-service-view--back-link-container {
    @media (max-width: $mobile-max-width) {
      border-color: $theme-color-primary-light;
    }
  }
  .required-service-view--back-link {
    color: $theme-color-primary;
  }

  .required-service-view--details-container-ready {
    background-color: $theme-color-primary-light;
  }
  .required-service-view--details-container-expired {
    background-color: $theme-color-gray-light;
  }
  .required-service-view--details-expired {
    border-color: $theme-color-gray-dark;
  }
  .required-service-view--details {
    border-color: $theme-color-primary-light-hover;
  }
  .required-service-view--details-last {
    border-color: $theme-color-primary-light-hover;
  }
  .required-service-view--price {
    font-size: $theme-txt-headline;
  }
  .required-service-view--image-container-ready {
    border-color: $theme-color-primary-light-hover;
  }
  .required-service-view--image-container-expired {
    border-color: $theme-color-gray-dark;
  }
  .required-service-view--expired-header {
    color: $theme-color-gray-darker;
  }
}
</style>
